import React, { useState } from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WorkIcon from '@mui/icons-material/Work';
import { Alert, Box, Button, CircularProgress, Container, Stack, Tab, Tabs, Typography } from '@mui/material';

import ChatterFeed from '~/components/Offers/Detail/OfferBuilder/Chatter/ChatterFeed';

import { useGetCase } from '~/hooks/apis/contracting/useGetCase';

import CaseDetailsTab from './CaseDetailsTab';
import CaseRelatedTab from './CaseRelatedTab';
import { casesErrorMessage } from './casesErrorMessage';
import { CASES_LABEL, CASES_PATH } from './constants';
import { SAMPLE_RELATED_RECORDS } from './mockCases';

const CASE_TABS = [
  { value: 'feed', label: 'Feed' },
  { value: 'details', label: 'Details' },
  { value: 'related', label: 'Related' },
] as const;

type CaseTab = (typeof CASE_TABS)[number]['value'];

// svc-contracting only serves partner cases, so a customer case is a 404 too.
function caseErrorMessage(error: Error, caseNumber: string | undefined): string {
  return casesErrorMessage(
    error,
    `case ${caseNumber}`,
    `Case ${caseNumber} isn't a partner case, or doesn't exist in Salesforce.`,
  );
}

export default function CaseDetailPage() {
  const { caseNumber } = useParams<{ caseNumber: string }>();
  const [tab, setTab] = useState<CaseTab>('feed');
  const { data: caseItem, isLoading, error } = useGetCase(caseNumber);

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>{`${CASES_LABEL} · ${caseNumber}`}</title>
      </Helmet>

      <Button
        component={Link}
        to={CASES_PATH}
        startIcon={<ArrowBackIcon />}
        size="small"
        sx={{ textTransform: 'none' }}
      >
        {CASES_LABEL}
      </Button>

      {isLoading && (
        <Box mt={2}>
          <CircularProgress />
        </Box>
      )}
      {error && (
        <Box mt={2}>
          <Alert severity="error">{caseErrorMessage(error, caseNumber)}</Alert>
        </Box>
      )}
      {caseItem && (
        <>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                bgcolor: 'secondary.main',
                color: 'common.white',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <WorkIcon />
            </Box>
            <Box>
              <Typography variant="h5" component="h1" fontWeight="bold">
                {caseItem.subject ? `${caseItem.case_number} · ${caseItem.subject}` : caseItem.case_number}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {caseItem.status}
                {caseItem.owner.name && ` · Owner: ${caseItem.owner.name}`}
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
            <Tabs value={tab} onChange={(_, value) => setTab(value)} aria-label="Case sections">
              {CASE_TABS.map((entry) => (
                <Tab key={entry.value} value={entry.value} label={entry.label} />
              ))}
            </Tabs>
          </Box>

          <Box sx={{ bgcolor: 'action.hover', p: 2, minHeight: 400 }}>
            {tab === 'feed' && (
              <ChatterFeed parentType="case" parentId={caseItem.external_refs.salesforce} scopeLabel="case" />
            )}
            {tab === 'details' && <CaseDetailsTab caseItem={caseItem} />}
            {tab === 'related' && (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Sample data: the case&apos;s emails, comments and attachments aren&apos;t connected to Salesforce yet.
                  Case History is live.
                </Alert>
                <CaseRelatedTab caseNumber={caseItem.case_number} sample={SAMPLE_RELATED_RECORDS} />
              </>
            )}
          </Box>
        </>
      )}
    </Container>
  );
}
